import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { recallRouteQuery } from '@/util/routeQueryMemory';

interface UpNavigationOptions {
  /**
   * Explicit "up" target. An absolute path (with or without its own query
   * string) is used verbatim. A relative `..`/`../..` drops that many trailing
   * segments. When omitted, the parent is the current path with its last
   * segment removed.
   */
  url?: string;
}

export const resolveTo = (url: string | undefined, pathname: string) => {
  // Explicit absolute target — the caller knows the exact destination.
  if (url && url.startsWith('/')) {
    return url;
  }

  // Relative `..` / omitted — compute the parent path and restore its
  // last-known query string (filters, selection) so the list comes back the
  // way the user left it.
  const levels = url ? url.split('/').filter((s) => s === '..').length || 1 : 1;
  const segments = pathname.split('/').filter(Boolean);
  segments.splice(Math.max(0, segments.length - levels));
  const path = `/${segments.join('/')}`;
  return `${path}${recallRouteQuery(path)}`;
};

/**
 * Deterministic "up" navigation for back buttons.
 *
 * The target is derived from the current route (the parent path), never from
 * browser history, so it stays correct after reloads, on deep links, and after
 * jumping between sections. For a computed parent, the list's last-known query
 * string (filters, selection) is restored from {@link recallRouteQuery}.
 */
export const useUpNavigation = ({ url }: UpNavigationOptions = {}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const to = useMemo(() => resolveTo(url, pathname), [url, pathname]);

  const goUp = useCallback(() => {
    navigate(to);
  }, [navigate, to]);

  return { to, goUp };
};

export default useUpNavigation;
